import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Save, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { legacyApi, LegacySupplier, PurchaseOrder, SaleOrder, Unit } from '../../../services/legacyApi';
import { api } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'sonner';
import { RequiredLabel } from '../../../components/forms/RequiredLabel';
import { FieldErrorText } from '../../../components/forms/FieldErrorText';
import { showValidationSummaryToast } from '../../../components/forms/ValidationSummaryToast';
import { focusFirstInvalidField, validateRequiredFields } from '../../../components/forms/formValidationUtils';
import { FlowEntryBanner } from '../../../components/workflow/FlowEntryBanner';
import EntityTypeaheadPicker, { PickerBadge, PickerOption } from '../../../components/search/EntityTypeaheadPicker';

interface LineItem {
    material_id?: number;
    description: string;
    unit_id?: number;
    unit_name?: string;
    quantity: number;
    unit_price: number;
    project_id?: number;
}

interface MaterialOption {
    material_id: number;
    material_name: string;
    item_type?: string;
    description?: string;
    unit_id?: number;
    unit?: string;
    base_unit_id?: number | null;
    base_unit_name?: string | null;
    uom_conversions?: Array<{
        from_unit_id: number;
        from_unit_name?: string | null;
        to_unit_id?: number;
        to_unit_name?: string | null;
        factor_to_base: number;
        is_base?: boolean;
    }>;
}

const normalizeItemType = (value?: string) => {
    const normalized = String(value || 'Stock').replace(/[-_\s]/g, '').toLowerCase();
    if (['consumable', 'expense', 'nonstock'].includes(normalized)) return 'Consumable';
    if (['service', 'services'].includes(normalized)) return 'Service';
    if (['fixedasset', 'asset', 'fixedassets'].includes(normalized)) return 'FixedAsset';
    return 'Stock';
};

const itemTypeTone = (itemType?: string): PickerBadge['tone'] => {
    const normalized = normalizeItemType(itemType);
    if (normalized === 'Consumable') return 'amber';
    if (normalized === 'Service') return 'blue';
    if (normalized === 'FixedAsset') return 'purple';
    return 'green';
};

export const LegacyPurchaseOrderForm: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isEdit = Boolean(id);
    const { hasPermission } = useAuth();

    const [suppliers, setSuppliers] = useState<LegacySupplier[]>([]);
    const [materials, setMaterials] = useState<MaterialOption[]>([]);
    const [units, setUnits] = useState<Unit[]>([]);
    const [projects, setProjects] = useState<SaleOrder[]>([]);
    const [loading, setLoading] = useState(false);

    const [supplierId, setSupplierId] = useState<number | ''>('');
    const [poDate, setPoDate] = useState<string>('');
    const [deliveryDate, setDeliveryDate] = useState<string>('');
    const [supplierQuotationNo, setSupplierQuotationNo] = useState('');
    const [deliveryAddress, setDeliveryAddress] = useState('Al Hadaf Fiberglass Ind LLC,Sajaa Industrial Area');
    const [deliveryPhoneNo, setDeliveryPhoneNo] = useState('+971586799393');
    const [paymentTerms, setPaymentTerms] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('');
    const [remarks, setRemarks] = useState('');
    const [discountAmount, setDiscountAmount] = useState(0);
    const [vat, setVat] = useState(5);
    const [lines, setLines] = useState<LineItem[]>([]);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        legacyApi.suppliers.list().then(setSuppliers).catch(console.error);
        api.materials.list().then((rows: any[]) => setMaterials(Array.isArray(rows) ? rows : [])).catch(console.error);
        legacyApi.masterData.listUnits().then(setUnits).catch(console.error);
        legacyApi.sales.list().then(setProjects).catch(console.error);
    }, []);

    const materialPickerOptions = useMemo<PickerOption<string>[]>(() => {
        return materials.map((material) => ({
            value: String(material.material_id),
            label: material.material_name,
            description: [
                `HC/MTR/${String(material.material_id).padStart(4, '0')}`,
                normalizeItemType(material.item_type),
                material.base_unit_name || material.unit,
            ].filter(Boolean).join(' | ') || undefined,
            badges: [{ label: normalizeItemType(material.item_type), tone: itemTypeTone(material.item_type) }],
        }));
    }, [materials]);

    const getMaterialMeta = (materialId?: number) => materials.find((m) => m.material_id === Number(materialId));

    const getAllowedUnits = (materialId?: number) => {
        const material = getMaterialMeta(materialId);
        return Array.isArray(material?.uom_conversions) ? material!.uom_conversions! : [];
    };

    const getConversionLabel = (line: LineItem) => {
        const material = getMaterialMeta(line.material_id);
        const selected = getAllowedUnits(line.material_id).find((row) => Number(row.from_unit_id) === Number(line.unit_id || material?.base_unit_id || 0));
        if (!material || !selected || selected.is_base || Number(selected.factor_to_base || 1) === 1) return '';
        return `1 ${selected.from_unit_name || ''} = ${selected.factor_to_base} ${material.base_unit_name || material.unit || ''}`.trim();
    };

    useEffect(() => {
        if (!isEdit || !id) return;
        legacyApi.purchasing.getPurchaseOrder(Number(id))
            .then((po) => {
                const supplierId = (po as PurchaseOrder & { supplier_id?: number }).supplier_id;
                setSupplierId(supplierId || '');
                setPoDate(po.po_date ? po.po_date.substring(0, 10) : '');
                setDeliveryDate(po.delivery_date ? po.delivery_date.substring(0, 10) : '');
                setSupplierQuotationNo(po.supplier_quotation_no || '');
                setDeliveryAddress(po.delivery_address || '');
                setDeliveryPhoneNo(po.delivery_phone_no || '');
                setPaymentTerms(po.payment_terms || '');
                setPaymentMethod(po.payment_method || '');
                setRemarks(po.remarks || '');
                setDiscountAmount(po.discount_amount || 0);
                setVat(po.vat || 0);
                setLines((po.details || []).map(d => ({
                    material_id: d.material_id,
                    description: d.material_name || d.description || '',
                    unit_id: d.unit_id,
                    unit_name: d.unit_name,
                    quantity: d.quantity,
                    unit_price: d.unit_price,
                    project_id: (d as any).project_id
                })));
            })
            .catch(console.error);
    }, [id, isEdit]);

    const totals = useMemo(() => {
        const subTotal = lines.reduce((sum, l) => sum + (l.quantity * l.unit_price), 0);
        const taxable = Math.max(subTotal - (discountAmount || 0), 0);
        const vatAmount = taxable * ((vat || 0) / 100);
        return { subTotal, taxable, vatAmount, grandTotal: taxable + vatAmount };
    }, [lines, discountAmount, vat]);

    const addLine = () => {
        setLines([...lines, { description: '', quantity: 0, unit_price: 0 }]);
    };

    const removeLine = (idx: number) => {
        setLines(lines.filter((_, i) => i !== idx));
    };

    const updateLine = (idx: number, patch: Partial<LineItem>) => {
        const next = [...lines];
        next[idx] = { ...next[idx], ...patch };
        setLines(next);
    };

    const handleSave = async () => {
        if (isEdit && !hasPermission('purchasing.po.edit')) {
            toast.error('You do not have permission to edit purchase orders');
            return;
        }
        if (!isEdit && !hasPermission('purchasing.po.create')) {
            toast.error('You do not have permission to create purchase orders');
            return;
        }
        const validation = validateRequiredFields([
            { key: 'supplier_id', label: 'Supplier', value: supplierId, required: true },
        ]);
        const nextErrors = { ...validation.errorsByField } as Record<string, string>;
        if (lines.length === 0) nextErrors.lines = 'Add at least one line item';
        if (Object.keys(nextErrors).length > 0) {
            setFieldErrors(nextErrors);
            showValidationSummaryToast();
            focusFirstInvalidField(Object.keys(nextErrors)[0]);
            return;
        }
        setFieldErrors({});

        setLoading(true);
        try {
            const payload = {
                supplier_id: Number(supplierId),
                po_date: poDate || undefined,
                delivery_date: deliveryDate || undefined,
                supplier_quotation_no: supplierQuotationNo || undefined,
                remarks: remarks || undefined,
                delivery_address: deliveryAddress || undefined,
                delivery_phone_no: deliveryPhoneNo || undefined,
                payment_terms: paymentTerms || undefined,
                payment_method: paymentMethod || undefined,
                discount_amount: discountAmount || 0,
                vat: vat || 0,
                details: lines.map(l => ({
                    material_id: l.material_id,
                    description: l.description,
                    unit_id: l.unit_id,
                    quantity: Number(l.quantity || 0),
                    unit_price: Number(l.unit_price || 0),
                    project_id: l.project_id || null
                }))
            };

            if (isEdit && id) {
                await legacyApi.purchasing.updatePurchaseOrder(Number(id), payload);
            } else {
                await legacyApi.purchasing.createPurchaseOrder({ ...payload, po_status_id: 1 });
            }
            navigate('/legacy/purchase-orders');
        } catch (error) {
            console.error(error);
            toast.error('Failed to save purchase order');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6 w-full p-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" onClick={() => navigate('/legacy/purchase-orders')}>
                        <ArrowLeft className="h-4 w-4 mr-2" /> Back
                    </Button>
                    <h1 className="text-2xl font-bold">{isEdit ? 'Edit Purchase Order' : 'New Purchase Order'}</h1>
                </div>
                <Button onClick={handleSave} disabled={loading || (isEdit ? !hasPermission('purchasing.po.edit') : !hasPermission('purchasing.po.create'))}>
                    <Save className="h-4 w-4 mr-2" /> {loading ? 'Saving...' : 'Save'}
                </Button>
            </div>

            <FlowEntryBanner
                title="Purchasing Flow: Purchase Order"
                pathLabel="PO to Supplier Bill"
                nextHint="Approve PO first, then receive GRN quantities before supplier billing."
                helpModule="purchasing"
            />

            <Card>
                <CardHeader><CardTitle>PO Header</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                        <RequiredLabel required htmlFor="supplier_id">Supplier</RequiredLabel>
                        <select
                            id="supplier_id"
                            name="supplier_id"
                            aria-invalid={!!fieldErrors.supplier_id}
                            aria-required="true"
                            value={supplierId}
                            onChange={(e) => setSupplierId(Number(e.target.value))}
                            className={`w-full h-10 px-3 rounded-md border bg-background text-sm ${fieldErrors.supplier_id ? 'border-red-500' : 'border-input'}`}
                        >
                            <option value="">Select Supplier</option>
                            {suppliers.map(s => (
                                <option key={s.supplier_id} value={s.supplier_id}>{s.supplier_name}</option>
                            ))}
                        </select>
                        <FieldErrorText message={fieldErrors.supplier_id} />
                    </div>
                    <div className="space-y-2">
                        <Label>PO Date</Label>
                        <Input type="date" value={poDate} onChange={(e) => setPoDate(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                        <Label>Delivery Date</Label>
                        <Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                        <Label>Supplier Quotation No</Label>
                        <Input value={supplierQuotationNo} onChange={(e) => setSupplierQuotationNo(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                        <Label>Delivery Address</Label>
                        <Input value={deliveryAddress} onChange={(e) => setDeliveryAddress(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                        <Label>Delivery Phone</Label>
                        <Input value={deliveryPhoneNo} onChange={(e) => setDeliveryPhoneNo(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                        <Label>Payment Terms</Label>
                        <Input value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                        <Label>Payment Method</Label>
                        <Input value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                        <Label>Discount Amount</Label>
                        <Input type="number" value={discountAmount} onChange={(e) => setDiscountAmount(Number(e.target.value))} />
                    </div>
                    <div className="space-y-2">
                        <Label>VAT %</Label>
                        <Input type="number" value={vat} onChange={(e) => setVat(Number(e.target.value))} />
                    </div>
                    <div className="space-y-2 md:col-span-3">
                        <Label>Remarks</Label>
                        <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} />
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <CardTitle>Line Items</CardTitle>
                        <Button onClick={addLine} variant="outline">
                            <Plus className="h-4 w-4 mr-2" /> Add Line
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-700 font-medium">
                            <tr>
                                <th className="p-2">Code</th>
                                <th className="p-2">Material</th>
                                <th className="p-2">Unit</th>
                                <th className="p-2">Project</th>
                                <th className="p-2">Description</th>
                                <th className="p-2 text-right">Qty</th>
                                <th className="p-2 text-right">Unit Price</th>
                                <th className="p-2 text-right">Sub Total</th>
                                <th className="p-2 text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {lines.map((line, idx) => (
                                <tr key={idx}>
                                    <td className="p-2 font-mono">
                                        {line.material_id ? `HC/MTR/${String(line.material_id).padStart(4, '0')}` : '-'}
                                    </td>
                                    <td className="p-2">
                                        <div className="relative z-20">
                                            <EntityTypeaheadPicker
                                                value={line.material_id ? String(line.material_id) : ''}
                                                options={materialPickerOptions}
                                                placeholder="Search material..."
                                                emptyText="No matching materials"
                                                onChange={(value) => {
                                                    const matId = Number(value);
                                                    if (!matId) return;
                                                    const mat = getMaterialMeta(matId);
                                                    const unitName = mat?.base_unit_name || mat?.unit;
                                                    updateLine(idx, {
                                                        material_id: matId,
                                                        description: mat?.material_name || line.description,
                                                        unit_id: mat?.base_unit_id || mat?.unit_id,
                                                        unit_name: unitName
                                                    });
                                                }}
                                            />
                                        </div>
                                    </td>
                                    <td className="p-2">
                                        <select
                                            value={line.unit_id || ''}
                                            onChange={(e) => {
                                                const nextUnitId = Number(e.target.value) || undefined;
                                                const nextUnit = getAllowedUnits(line.material_id).find(u => u.from_unit_id === nextUnitId);
                                                updateLine(idx, {
                                                    unit_id: nextUnitId,
                                                    unit_name: nextUnit?.from_unit_name || nextUnit?.to_unit_name || undefined
                                                });
                                            }}
                                            className="w-full h-9 px-2 rounded-md border border-input bg-background text-sm"
                                            disabled={!line.material_id}
                                        >
                                            <option value="">Select unit</option>
                                            {getAllowedUnits(line.material_id).map(u => (
                                                <option key={u.from_unit_id} value={u.from_unit_id}>{u.from_unit_name || u.to_unit_name}</option>
                                            ))}
                                        </select>
                                        {getConversionLabel(line) && (
                                            <div className="mt-1 text-[11px] text-slate-500">{getConversionLabel(line)}</div>
                                        )}
                                    </td>
                                    <td className="p-2">
                                        <select
                                            value={line.project_id || ''}
                                            onChange={(e) => updateLine(idx, { project_id: Number(e.target.value) || undefined })}
                                            className="w-full h-9 px-2 rounded-md border border-input bg-background text-sm"
                                        >
                                            <option value="">General Stock / Overhead</option>
                                            {projects.map(p => (
                                                <option key={p.so_id} value={p.so_id}>
                                                    {p.so_number} - {p.customer_name?.substring(0, 20)}
                                                </option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="p-2">
                                        <Input value={line.description} onChange={(e) => updateLine(idx, { description: e.target.value })} />
                                    </td>
                                    <td className="p-2 text-right">
                                        <Input type="number" value={line.quantity} onChange={(e) => updateLine(idx, { quantity: Number(e.target.value) })} />
                                    </td>
                                    <td className="p-2 text-right">
                                        <Input type="number" value={line.unit_price} onChange={(e) => updateLine(idx, { unit_price: Number(e.target.value) })} />
                                    </td>
                                    <td className="p-2 text-right">{(line.quantity * line.unit_price).toFixed(2)}</td>
                                    <td className="p-2 text-center">
                                        <Button variant="ghost" size="icon" onClick={() => removeLine(idx)}>
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            {lines.length === 0 && (
                                <tr>
                                    <td colSpan={9} className="p-4 text-center text-muted-foreground">No line items</td>
                                </tr>
                            )}
                            {fieldErrors.lines && (
                                <tr>
                                    <td colSpan={9} className="px-3 pb-2">
                                        <FieldErrorText message={fieldErrors.lines} />
                                    </td>
                                </tr>
                            )}
                        </tbody>
                        <tfoot className="bg-slate-50 font-semibold">
                            <tr>
                                <td colSpan={5} className="p-2 text-right">Sub Total</td>
                                <td className="p-2 text-right">{totals.subTotal.toFixed(2)}</td>
                                <td />
                            </tr>
                            <tr>
                                <td colSpan={5} className="p-2 text-right">Discount</td>
                                <td className="p-2 text-right">{discountAmount.toFixed(2)}</td>
                                <td />
                            </tr>
                            <tr>
                                <td colSpan={5} className="p-2 text-right">VAT</td>
                                <td className="p-2 text-right">{totals.vatAmount.toFixed(2)}</td>
                                <td />
                            </tr>
                            <tr>
                                <td colSpan={5} className="p-2 text-right">Grand Total</td>
                                <td className="p-2 text-right">{totals.grandTotal.toFixed(2)}</td>
                                <td />
                            </tr>
                        </tfoot>
                    </table>
                </CardContent>
            </Card>
        </div>
    );
};
